import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api, ApiAuthError, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { roleLabel } from '../lib/roles';
import { humanizeToken } from '../lib/inlineAttribution';
import { PageHeader } from '../shell';
import { DataTable, CreateEntityModal, PermissionsEditor, StatCard, StatCardRow, Modal, useToast } from '../organisms';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * UserManagement — /v2/user-management
 *
 * Super_admin/admin surface for managing users + security groups.
 * Mirrors the v1 SPA loadUserManagement() layout:
 *   - Org chart (super_admin → admin → agent/analyst) above the fold
 *   - Users table with Edit + Permissions actions
 *   - Security Groups table with base_role dropdown + Edit Permissions
 *
 * Both "+ New User" and "+ New Role" use CreateEntityModal. Permissions
 * editor is a stub for this PR (lands in a follow-up).
 *
 * Phase 11 Block 11 PR 1 (2026-05-12).
 */

interface CurrentUser { id: string; role: string; }

// Every role the users.role / security_groups.base_role enums can hold — kept in
// lock-step with src/config/enums.js so the badge + org-chart never renders an
// `undefined` variant/color for a real row. The internal staff tiers plus the
// external roles: 'consultant' (migration 029), 'training' (migration 087, this
// lane), and the portal 'client' tier.
type Role =
  | 'super_admin' | 'admin' | 'administrator' | 'analyst' | 'agent'
  | 'consultant' | 'training' | 'client';

// The four internal tiers rendered in the org chart.

interface UserListRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: Role;
  employee_title: string | null;
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  role: Role;
  status: string | null;
  last_login_at: string | null;
  security_group_id: string | null;
  contact_id: string;
  is_active: boolean | number;
}

interface SecurityGroup {
  id: string;
  name: string;
  base_role: Role;
}

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

const roleVariant: Record<Role, 'success' | 'warning' | 'info' | 'neutral'> = {
  super_admin: 'warning',
  admin: 'info',
  administrator: 'info',
  agent: 'success',
  analyst: 'neutral',
  consultant: 'info',
  training: 'neutral',
  client: 'neutral',
};

// Every role → a proper Title Case label via the shared humanizer (super_admin →
// "Super Admin", administrator → "Office Administrator", and agent/analyst/consultant/
// etc. each capitalized) so no surface shows a raw or lowercase token. Null-safe: a
// security group's base_role is nullable in the schema — a raw String call here once
// crashed the whole page — so guard null → "—".
const labelForRole = (r: Role | null | undefined): string => (r ? roleLabel(r) : '—');

interface InviteResult {
  email: string;
  invite_token: string;
  invite_expires_at: string;
}

export function UserManagement() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [permsEditing, setPermsEditing] = useState<{ id: string; name: string } | null>(null);
  const [invite, setInvite] = useState<InviteResult | null>(null);

  // Mint a single-use invite token for a passwordless user. The user then sets
  // their own password via POST /api/auth/set-password with { email, invite_token,
  // new_password }. super_admin-gated + org-scoped on the backend.
  const inviteMutation = useMutation<InviteResult, Error, string>({
    mutationFn: (userId) => api.post<InviteResult>(`/api/admin/users/${userId}/invite`, {}),
    onSuccess: (res) => setInvite(res),
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const currentUserQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  const usersListQuery = useQuery<UserListRow[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<UserListRow[]>('/api/users-list', signal),
  });

  const usersQuery = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: ({ signal }) => api.get<User[]>('/api/users', signal),
  });

  const groupsQuery = useQuery<SecurityGroup[]>({
    queryKey: ['security-groups'],
    queryFn: ({ signal }) => api.get<SecurityGroup[]>('/api/security-groups', signal),
  });

  // Pre-fetch contacts for the New User FK dropdown
  const contactsQuery = useQuery<Contact[]>({
    queryKey: ['contacts'],
    queryFn: ({ signal }) => api.get<Contact[]>('/api/contacts', signal),
  });

  const usersList = usersListQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const groups = groupsQuery.data ?? [];
  const contacts = contactsQuery.data ?? [];

  // Role gate — super_admin only. Users, security groups, and the permission
  // matrix are super_admin-only at the API (SoD: SUPER_ADMIN_ONLY_TABLES), so an
  // admin reaching this page would only hit 403s on every query. Keeping the page
  // gate in lock-step with the backend is the correct mapping (defense-in-depth
  // alongside the RequireSuperAdmin route guard).
  if (currentUserQuery.data && currentUserQuery.data.role !== 'super_admin') {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>User management is restricted to super admins.</p>
      </div>
    );
  }

  if (usersListQuery.isLoading || usersQuery.isLoading || groupsQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading users…</p>;
  }

  const activeUsers = users.filter(u => (u.status ? u.status === 'active' : Boolean(u.is_active))).length;
  const adminUsers = users.filter(u => ['super_admin', 'admin', 'administrator'].includes(u.role)).length;

  // Merge users-list (names) into users (auth fields) for the table
  const usersTableData = users.map(u => {
    const name = usersList.find(x => x.id === u.id);
    return {
      ...u,
      first_name: name?.first_name ?? null,
      last_name: name?.last_name ?? null,
    };
  });

  // 2.4 (Yadi 7/30): contacts are external; USERS are internal / delivery-side. Client-
  // portal users (role='client') get their OWN section, out of the internal-staff list —
  // office admins / agents / consultants stay under Internal users (consultants are
  // users, not contacts). A client is created in contacts + classified by level; when
  // granted portal access it also becomes a client-portal user shown here.
  const internalUsers = usersTableData.filter(u => u.role !== 'client');
  const portalUsers = usersTableData.filter(u => u.role === 'client');

  const userColumns: ColumnDef<typeof usersTableData[number]>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => [row.original.first_name, row.original.last_name].filter(Boolean).join(' ') || row.original.email,
    },
    { accessorKey: 'email', header: 'Email' },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ getValue }) => {
        const r = getValue() as Role;
        return <Badge variant={roleVariant[r]} dot>{labelForRole(r)}</Badge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue, row }) => {
        const s = String(getValue() || (row.original.is_active ? 'active' : 'inactive'));
        return <Badge variant={s === 'active' ? 'success' : 'neutral'}>{humanizeToken(s)}</Badge>;
      },
    },
    {
      accessorKey: 'last_login_at',
      header: 'Last login',
      cell: ({ getValue }) => formatDateTime(getValue() as string | null),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          loading={inviteMutation.isPending && inviteMutation.variables === row.original.id}
          onClick={(e) => {
            // Stop the row-click navigation; minting an invite is its own action.
            e.stopPropagation();
            inviteMutation.mutate(row.original.id);
          }}
        >
          Invite
        </Button>
      ),
    },
  ];

  const groupColumns: ColumnDef<SecurityGroup>[] = [
    { accessorKey: 'name', header: 'Name' },
    {
      accessorKey: 'base_role',
      header: 'Base role',
      cell: ({ getValue }) => {
        const r = getValue() as Role;
        return <Badge variant={roleVariant[r]} dot>{labelForRole(r)}</Badge>;
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Users & roles"
        subtitle={`${users.length} user${users.length === 1 ? '' : 's'} · ${groups.length} role${groups.length === 1 ? '' : 's'}`}
        actions={
          <>
            <Button variant="secondary" size="md" onClick={() => setCreateGroupOpen(true)}>
              + New Role
            </Button>
            <Button variant="primary" size="md" onClick={() => setCreateUserOpen(true)}>
              + New User
            </Button>
          </>
        }
      />

      <CreateEntityModal
        open={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        table="users"
        fkOptions={{
          contact_id: contacts.map(c => ({
            value: c.id,
            label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || c.id.slice(0, 8),
          })),
          security_group_id: groups.map(g => ({ value: g.id, label: `${g.name} (${labelForRole(g.base_role)})` })),
        }}
        invalidateKeys={[['users'], ['users-list']]}
      />

      <CreateEntityModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        table="security_groups"
        routePath="security-groups"
        invalidateKeys={[['security-groups']]}
      />

      <section style={{ marginBottom: 'var(--sp-6)' }}>
        <StatCardRow>
          <StatCard label="Users" value={String(users.length)} caption="total accounts" accent="hunter" />
          <StatCard label="Active" value={String(activeUsers)} caption="enabled accounts" accent="green" />
          <StatCard label="Admins" value={String(adminUsers)} caption="super admin · admin" accent="amber" />
        </StatCardRow>
      </section>

      <section style={{ marginBottom: 'var(--sp-6)' }}>
        <SectionHeader title="Internal users" count={internalUsers.length} />
        <DataTable
          data={internalUsers}
          columns={userColumns}
          onRowClick={(row) => navigate(`/users/${row.id}`)}
        />
      </section>

      <section style={{ marginBottom: 'var(--sp-6)' }}>
        <SectionHeader title="Business portal users" count={portalUsers.length} />
        {portalUsers.length === 0 ? (
          <Empty message="No client portal users yet. A client gains a portal login from its Client Portal tab — the contact record stays; a portal-user record is created here." />
        ) : (
          <DataTable
            data={portalUsers}
            columns={userColumns}
            onRowClick={(row) => navigate(`/users/${row.id}`)}
          />
        )}
      </section>

      <section>
        <SectionHeader title="Roles" count={groups.length} />
        {groups.length === 0 ? (
          <Empty message="No roles yet. Use + New Role to create one." />
        ) : (
          <DataTable
            data={groups}
            columns={groupColumns}
            onRowClick={(row) => setPermsEditing({ id: row.id, name: row.name })}
          />
        )}
        <p style={{
          marginTop: 'var(--sp-2)',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-sm)',
        }}>
          Click a row to edit its permissions grid (12 entities × 6 actions).
        </p>
      </section>

      <PermissionsEditor
        open={permsEditing !== null}
        onClose={() => setPermsEditing(null)}
        groupId={permsEditing?.id ?? null}
        groupName={permsEditing?.name ?? ''}
      />

      <Modal
        open={invite !== null}
        onClose={() => setInvite(null)}
        title="Invite created"
        size="sm"
        footer={<Button variant="primary" onClick={() => setInvite(null)}>Done</Button>}
      >
        {invite && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <p style={{ margin: 0, color: 'var(--color-ink-2)' }}>
              Share this single-use token with <strong>{invite.email}</strong>. They set their
              password via the set-password flow before it expires.
            </p>
            <label style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              Invite token
              <input
                readOnly
                value={invite.invite_token}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  width: '100%',
                  marginTop: 'var(--sp-1)',
                  padding: 'var(--sp-2)',
                  fontFamily: 'var(--font-mono, monospace)',
                  fontSize: 'var(--fs-xs)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--r-md)',
                  background: 'var(--color-card)',
                  color: 'var(--color-ink)',
                }}
              />
            </label>
            <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Expires {formatDateTime(invite.invite_expires_at)}.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}


function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <header style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 'var(--sp-2)',
      marginBottom: 'var(--sp-3)',
    }}>
      <h2 style={{
        margin: 0,
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
      }}>{title}</h2>
      <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>· {count}</span>
    </header>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
